import { fetchOneEntry, isEditing, isPreviewing } from '@builder.io/sdk-vue'
import type { BuilderContent } from '@builder.io/sdk-vue'
import { toValue, computed } from 'vue'
import type { MaybeRefOrGetter } from 'vue'

interface UseBuilderPageOptions {
  model?: MaybeRefOrGetter<string>
  extraFetchProps?: MaybeRefOrGetter<Record<string, unknown> | undefined>
  userAttributes?: MaybeRefOrGetter<Record<string, unknown> | undefined>
  shouldThrow?: boolean
}

export const useBuilderPage = async (options: UseBuilderPageOptions = {}) => {
  const config = useRuntimeConfig()
  const route = useRoute()
  const { localeIsoCode } = useLocaleIso()
  const BUILDER_PUBLIC_API_KEY = config.public.builderio.publicKey

  const shouldThrow = options.shouldThrow !== false
  const model = computed(() => toValue(options.model) || 'page')
  const userAttributes = computed(() => toValue(options.userAttributes) || {})
  const extraFetchProps = computed(() => toValue(options.extraFetchProps) || {})

  const currentPath = route.path

  const asyncKey = computed(() => {
    const serializedAttributes = JSON.stringify(userAttributes.value)
    return `builder-reactive-${model.value}-${currentPath}-${localeIsoCode.value}-${serializedAttributes}`
  })

  const { data, error } = await useAsyncData<BuilderContent | null>(
    asyncKey,
    async () => {
      return fetchOneEntry({
        model: model.value,
        apiKey: BUILDER_PUBLIC_API_KEY,
        apiVersion: 'v3',
        userAttributes: {
          urlPath: currentPath,
          locale: localeIsoCode.value,
          ...userAttributes.value,
        },
        locale: localeIsoCode.value,
        cacheSeconds: import.meta.dev ? 0 : 5 * 60,
        staleCacheSeconds: import.meta.dev ? 0 : 10 * 60,
        ...extraFetchProps.value,
      })
    },
    { watch: [asyncKey] }
  )

  const isBuilderUI = isEditing(currentPath) || isPreviewing(currentPath)

  if (!isBuilderUI) {
    if (error.value) {
      const fetchError = error.value as {
        statusCode?: number
        message?: string
      }
      throw createError({
        statusCode: fetchError?.statusCode || 500,
        statusMessage:
          fetchError?.message || 'Failed to fetch content from CMS engine.',
        fatal: true,
      })
    }

    if (!data.value && shouldThrow) {
      throw createError({
        statusCode: 404,
        statusMessage: `Page '${currentPath}' not found in Builder.io model '${model.value}'`,
        fatal: true,
      })
    }
  }

  return { data }
}
